import { WorkbookPage, SectionTitle, Paragraph, WorksheetInput } from '@/components/WorkbookPage';
import { AlertTriangle, CheckCircle } from 'lucide-react';

export const CrisisModePage = () => {
  return (
    <WorkbookPage pageId="crisis-mode" prevPage="irregular-planner" nextPage="reset-page">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-warning" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Crisis Mode Rules
          </h1>
        </div>
        <p className="text-muted-foreground">
          What to do when everything falls apart
        </p>
      </header>
      
      <div className="bg-warning/10 border border-warning/30 rounded-xl p-6 mb-8">
        <Paragraph>
          Life happens. Job loss, medical emergency, unexpected crisis—sometimes everything 
          you've built feels like it's crumbling. These rules help you survive until you can 
          get back on your feet. <strong>This is temporary. You will get through this.</strong>
        </Paragraph>
      </div>
      
      <SectionTitle>Crisis Priority Order</SectionTitle>
      <Paragraph>
        When money is extremely tight, pay for things in this order:
      </Paragraph>
      
      <div className="space-y-4 my-6">
        {[
          { num: 1, title: 'Food', desc: 'You need to eat. Basic groceries, not restaurants.' },
          { num: 2, title: 'Shelter', desc: 'Rent/mortgage. Keeping a roof over your head is essential.' },
          { num: 3, title: 'Utilities', desc: 'Lights, water, heat/cooling. Contact providers about hardship programs.' },
          { num: 4, title: 'Transportation', desc: 'What you need to get to work. Car payment, gas, bus pass.' },
          { num: 5, title: 'Medicine', desc: 'Essential medications. Ask about generics and patient assistance programs.' },
        ].map((item) => (
          <div key={item.num} className="flex items-start gap-4 bg-card border border-border rounded-lg p-4">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
              {item.num}
            </div>
            <div>
              <h4 className="font-semibold">{item.title}</h4>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
      
      <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-5 my-8">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          What CAN Wait (if necessary):
        </h3>
        <ul className="space-y-2 text-sm">
          <li>• Credit card payments (will hurt your credit but won't leave you homeless)</li>
          <li>• Student loans (contact them about forbearance/deferment)</li>
          <li>• Collections (they can wait—focus on survival)</li>
          <li>• Subscriptions (cancel everything non-essential)</li>
          <li>• "Nice to haves" (gym, streaming, etc.)</li>
        </ul>
      </div>
      
      <SectionTitle>Crisis Action Checklist</SectionTitle>
      
      <div className="space-y-3 my-6">
        {[
          'Contact landlord/mortgage company BEFORE you miss a payment',
          'Call utility companies about hardship programs',
          'Apply for any assistance you qualify for (SNAP, unemployment, etc.)',
          'Contact creditors to negotiate or pause payments',
          'Cancel all non-essential subscriptions immediately',
          'Look for any way to increase income (sell items, gig work)',
          'Reach out to community resources (churches, food banks, etc.)',
          'Tell someone you trust what\'s happening—don\'t suffer alone',
        ].map((item, index) => (
          <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
            <CheckCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <span className="text-sm">{item}</span>
          </div>
        ))}
      </div>
      
      <SectionTitle>Resources to Know About</SectionTitle>
      
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">211</h4>
          <p className="text-sm text-muted-foreground">
            Dial 211 or visit 211.org for local assistance programs
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Food Banks</h4>
          <p className="text-sm text-muted-foreground">
            FeedingAmerica.org to find local food assistance
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Utility Assistance</h4>
          <p className="text-sm text-muted-foreground">
            LIHEAP and local programs help with energy bills
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Rent/Housing Help</h4>
          <p className="text-sm text-muted-foreground">
            Contact local housing authority, 211, or community organizations
          </p>
        </div>
      </div>
      
      <div className="mt-8 bg-primary/5 rounded-xl p-6 text-center">
        <p className="font-serif text-lg italic mb-2">
          "This too shall pass."
        </p>
        <p className="text-sm text-muted-foreground">
          Crisis is temporary. Focus on survival, then rebuild. You've got this.
        </p>
      </div>
      
      <div className="mt-8">
        <WorksheetInput
          label="My crisis notes (numbers to call, plans made, etc.):"
          placeholder="Use this space to track your crisis plan..."
          multiline
        />
      </div>
    </WorkbookPage>
  );
};
